import { Component, OnDestroy, OnInit } from '@angular/core';

import { TranslocoService } from '@ngneat/transloco';

import { Observable, Subscription, map, of, combineLatest, throwError } from 'rxjs';
import { filter, tap, mergeMap } from 'rxjs/operators';

import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { FundingInfoUtils } from 'src/app/core/services/loan-application/funding-info/funding-info-utils';
import { DisbursementUtils } from 'src/app/core/services/loan-application/disbursement/disbursement-utils';
import { ILoanApplication } from 'src/app/core/services/loan-application/loan-application.model';
import { LoanApplicationService } from 'src/app/core/services/loan-application/loan-application.service';
import { AddressTypeEnum, IAddressResult, MobileApiService } from 'src/app/core/services/mobile-api';
import { DisbursementType } from 'src/app/core/services/loan-application/disbursement/disbursement.model';

import { RoutingPathsEnum, RoutingService } from 'src/app/core/services/routing/routing.service';
import {
	AchBankAccountsService,
	IAchBankAccount
} from 'src/app/core/services/ach-bank-accounts/ach-bank-accounts.service';
import { MessageConfirmDialogComponent } from 'src/app/shared/components/dialogs/message-confirm-dialog/message-confirm-dialog.component';

import { ConfirmTermsService } from '../../terms/confirm-terms.service';

@Component({
	selector: 'op-expedite-disbursement',
	templateUrl: './expedite-disbursement.component.html',
	styleUrls: ['./expedite-disbursement.component.scss']
})
export class ExpediteDisbursementComponent implements OnInit, OnDestroy {
	private subscription = new Subscription();

	constructor(
		private loanAppService: LoanApplicationService,
		private mobileApiService: MobileApiService,
		private bankAccountService: AchBankAccountsService,
		private routingService: RoutingService,
		private translocoService: TranslocoService,
		private dialogService: DialogService,
		private confirmTermService: ConfirmTermsService
	) {}

	readonly disbursementType: typeof DisbursementType = DisbursementType;

	disbursement: DisbursementUtils;
	fundingInfo: FundingInfoUtils;
	loanApp: ILoanApplication = null;
	switchToDebitCardEligible: boolean = true;
	switchToACHEligible: boolean = false;
	mailingAddress: string | null = null;
	disbursementAccount: IAchBankAccount | null = null;

	loaded: boolean = false;

	ngOnInit(): void {
		const loanAppSub = this.loanAppService.loanApplication$
			.pipe(
				filter(Boolean),
				tap((loanApp: ILoanApplication) => {
					this.loanApp = loanApp;
					this.fundingInfo = FundingInfoUtils.fromLoanApp(loanApp);
					this.disbursement = DisbursementUtils.fromLoanApp(loanApp);
					if (this.disbursement.isDebitCard()) {
						return throwError(() => {
							return 'eligibility';
						});
					}
				}),
				mergeMap(() => {
					const debitCardEligibility = this.mobileApiService.getDebitCardEligibility(this.loanApp.id, 0, {
						applicationType: this.loanApp.applicationType
					});
					return combineLatest([debitCardEligibility, this.getDisbursementDetails()]);
				})
			)
			.subscribe({
				next: ([debitCardEligibility, disbursementDetails]): void => {
					this.mailingAddress = disbursementDetails;
					this.switchToDebitCardEligible = debitCardEligibility?.eligible && !this.disbursement.isDebitCard();
					this.switchToACHEligible = !this.switchToDebitCardEligible && this.disbursement.isTypeCheck();
					this.loaded = true;
				},
				error: (_error) => {
					if (_error === 'eligibility') {
						this.continueOnlineProcess();
					}
				}
			});
		this.subscription.add(loanAppSub);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	continueOnlineProcess(): void {
		this.confirmTermService.getNextRoute().subscribe({
			next: (route) => {
				this.routingService.route(route);
			}
		});
	}

	switchToDebitCard(): void {
		this.showDebitCardDisbursementAuthorization().subscribe({
			next: (result) => {
				if (result) {
					this.routingService.route(RoutingPathsEnum.expediteWithDebitCard);
				}
			}
		});
	}

	switchToDirectDeposit(): void {
		this.routingService.route(RoutingPathsEnum.expediteWithACH);
	}

	getDisbursementDetails(): Observable<string | null> {
		if (this.disbursement.isTypeCheck()) {
			return this.mobileApiService
				.getAddresses(this.loanApp.id, this.loanAppService.getCurrentApplicantUtils()?.getCurrentApplicantIndex())
				.pipe(
					map((addresses: IAddressResult[]) => {
						return addresses.find((address: IAddressResult) => address.type === AddressTypeEnum.mail);
					}),
					map((address: IAddressResult) => {
						this.disbursementAccount = null;
						if (address.streetAddress2) {
							return `${address.streetAddress1} ${address.streetAddress2 || ''}, ${address.city}, ${address.state} ${
								address.postalCode
							}`;
						} else {
							return `${address.streetAddress1}, ${address.city}, ${address.state} ${address.postalCode}`;
						}
					})
				);
		} else if (this.disbursement.isTypeAch()) {
			return this.bankAccountService.bankAccounts$.pipe(
				filter(Boolean),
				map((accounts: IAchBankAccount[]) => {
					return accounts.find((account) => account.selected);
				}),
				map((account: IAchBankAccount) => {
					this.disbursementAccount = account;
					return null;
				})
			);
		} else {
			return of(null);
		}
	}

	showDebitCardDisbursementAuthorization(): Observable<boolean> {
		const data = {
			title: this.translocoService.translate('DISBURSEMENT_SELECTION.DEBIT_CARD.AUTHORIZATION.title'),
			message: this.translocoService.translate('DISBURSEMENT_SELECTION.DEBIT_CARD.AUTHORIZATION.message'),
			confirmText: this.translocoService.translate('GLOBAL.continue')
		};
		return this.dialogService.openSimpleDialog(MessageConfirmDialogComponent, { data }).pipe(
			map((result) => {
				return Boolean(result);
			})
		);
	}
}
